<script>

import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";

import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";

import agentsAdded from "../management/plugins/agentsAdded";
import registriesByAgent from "../management/plugins/registriesByAgent";
import axios from "axios";
import Swal from "sweetalert2";
import jwt_decode from "jwt-decode";

export default {
  page: {
    title: "Analytics",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      title: "Analytics",
      items: [
        {
          text: "Dashboards",
          href: "/",
        },
        {
          text: "Analytics",
          active: true,
        },
      ],
      departmentList: {},
      department: '0',
      dateRange: moment().startOf('month').format("DD MMM, YYYY") + " to " + moment().endOf('month').format("DD MMM, YYYY"),
      rangeDateconfig: {
        mode: "range",
        dateFormat: "d M, Y",
        defaultDate: moment().startOf('month').format("DD MMM, YYYY") + " to " + moment().endOf('month').format("DD MMM, YYYY")
      },
      totalAgents: 0,
      totalClients: 0,
      totalProspects: 0,
    };
  },
  components: {
    Layout,
    PageHeader,
    agentsAdded,
    registriesByAgent,
    flatPickr,
  },
  methods: {
    viewUserGroup(request) {
      const currentRole = jwt_decode(localStorage.getItem('jwt'));
      return currentRole.userGroup.includes(request);
    }
  },
  async mounted() {
    const data = {
      token: localStorage.getItem('jwt')
    };

    await axios
      .post('/api/listDepartments', data)
      .then(response => {
        this.departmentList = response.data;
      })
      .catch(error => {
        if (error.response) {
          if (error.response.data.detail === 'Your session is expired!') {
            this.$router.push('logout');
          }
          Swal.fire("Oops!", "Departments could not be loaded, please contact your SysAdmin.", "error");
        }
      });
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <!-- Welcome Header & Banner -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm rounded-3 overflow-hidden bg-soft-primary" style="background-image: linear-gradient(135deg, rgba(64,81,137,0.08) 0%, rgba(53,119,241,0.05) 100%);">
          <div class="card-body p-4">
            <div class="row align-items-center">
              <div class="col-sm-8">
                <h4 class="fw-semibold text-primary mb-1">Welcome to Analytics Dashboard</h4>
                <p class="text-muted mb-0 fs-14">Track recruitment metrics, agents registration channels, and active agent directories in real-time.</p>
              </div>
              <div class="col-sm-4 text-sm-end text-start mt-sm-0 mt-3">
                <span class="badge bg-soft-info text-info px-3 py-2 fs-12 rounded-pill">
                  <i class="ri-time-line align-middle me-1"></i> Live Performance Tracking
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Unified Filters Bar -->
    <div class="card mb-4 border-0 shadow-sm rounded-3">
      <div class="card-body bg-light p-3 rounded-3">
        <div class="row g-3 align-items-end">
          <div class="col-md-4 col-sm-6" v-if="viewUserGroup('SUPER_ADMIN_GROUP')">
            <label class="form-label text-muted fw-semibold mb-1 fs-12 text-uppercase">Filter Department</label>
            <select class="form-select border-0 shadow-sm"
                    aria-label="Department"
                    id="filterDepartment"
                    name="filterDepartment"
                    v-model="department">
              <option value="0">All Departments</option>
              <option v-for="(dept, index) of departmentList" :key="index" :value="dept.departmentID">
                {{ dept.name }}
              </option>
            </select>
          </div>
          <div class="col-md-4 col-sm-6">
            <label class="form-label text-muted fw-semibold mb-1 fs-12 text-uppercase">Global Date Range</label>
            <flat-pickr
              v-model="dateRange"
              placeholder="Select date range"
              :config="rangeDateconfig"
              class="form-control border-0 shadow-sm"
            ></flat-pickr>
          </div>
        </div>
      </div>
    </div>

    <!-- Metrics Cards -->
    <div class="row mb-4">
      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Total Agents Added</p>
                <h2 class="mb-0 text-primary fw-bold">{{ totalAgents }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center fs-24" style="width: 48px; height: 48px;">
                  <i class="ri-user-add-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-primary position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>
      
      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Total Clients Added</p>
                <h2 class="mb-0 text-success fw-bold">{{ totalClients }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-success text-success rounded-circle d-flex align-items-center justify-content-center fs-24" style="width: 48px; height: 48px;">
                  <i class="ri-user-follow-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-success position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card card-animate border-0 shadow-sm rounded-3 h-100 position-relative overflow-hidden">
          <div class="card-body p-4">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <p class="text-uppercase fw-semibold text-muted mb-2 fs-12">Total Prospects Added</p>
                <h2 class="mb-0 text-warning fw-bold">{{ totalProspects }}</h2>
              </div>
              <div class="flex-shrink-0">
                <div class="avatar-sm bg-soft-warning text-warning rounded-circle d-flex align-items-center justify-content-center fs-24" style="width: 48px; height: 48px;">
                  <i class="ri-user-search-line"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="card-border bg-warning position-absolute bottom-0 start-0 end-0" style="height: 3px;"></div>
        </div>
      </div>
    </div>

    <!-- Reports Section -->
    <div class="row">
      <div class="col-xl-5 col-md-4 mb-4">
        <agentsAdded 
          :department="department" 
          :dateRange="dateRange"
          @update-total-agents="totalAgents = $event"
        />
      </div>
      <div class="col-xl-7 col-md-8 mb-4">
        <registriesByAgent 
          :department="department" 
          :dateRange="dateRange"
          @update-totals="totalClients = $event.clients; totalProspects = $event.prospects"
        />
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.card-animate {
  transition: all 0.3s ease;
}
.card-animate:hover {
  transform: translateY(-4px);
}
</style>
